import { motion, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useRef, useState } from 'react';
import useMagneticEffect from '../hooks/useMagneticEffect';
import MorphingBlobs from './MorphingBlobs';

const FeaturedWork = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const buttonRef = useMagneticEffect(0.4);

  const projects = [
    {
      title: 'Karat',
      category: 'Corporate Website',
      image: 'https://images.unsplash.com/photo-1767788543704-d68ce083048e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB3ZWJzaXRlJTIwZGVzaWdufGVufDB8fHx8MTc2OTE4NjYxNHww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Cellares',
      category: 'Corporate Website',
      image: 'https://images.unsplash.com/photo-1621857093087-7daa85ab14a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB3ZWJzaXRlJTIwZGVzaWdufGVufDB8fHx8MTc2OTE4NjYxNHww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Lumana',
      category: 'Corporate Website',
      image: 'https://images.unsplash.com/photo-1502945015378-0e284ca1a5be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB3ZWJzaXRlJTIwZGVzaWdufGVufDB8fHx8MTc2OTE4NjYxNHww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Ambi Robotics',
      category: 'Corporate Website',
      image: 'https://images.unsplash.com/photo-1706700392626-5279fb90ae73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjB3ZWJzaXRlJTIwZGVzaWdufGVufDB8fHx8MTc2OTE4NjYxNHww&ixlib=rb-4.1.0&q=85'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  return (
    <section id="work" ref={ref} className="bg-[#0f0f0f] py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 relative overflow-hidden">
      {/* Morphing blobs background */}
      <MorphingBlobs />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-14 md:mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white text-xs sm:text-sm md:text-base tracking-[0.3em] sm:tracking-[0.4em] mb-3 sm:mb-4 font-light"
          >
            FEATURED WORK
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-base sm:text-lg"
          >
            Explore some of our latest website projects.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-12 sm:mb-14 md:mb-16"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              variants={itemVariants}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="group cursor-pointer will-change-transform"
              data-cursor="view"
            >
              <div className="relative overflow-hidden rounded-lg mb-4 sm:mb-5 md:mb-6 aspect-[3/4] will-change-transform">
                <motion.div className="relative w-full h-full">
                  <motion.img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    className="w-full h-full object-cover will-change-transform"
                    animate={{
                      scale: hoveredIndex === index ? 1.1 : 1
                    }}
                    transition={{ duration: 0.6, ease: [0.6, 0.05, 0.01, 0.9] }}
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                      opacity: hoveredIndex === index ? 1 : 0,
                      y: hoveredIndex === index ? 0 : 20
                    }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8 text-white"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white flex items-center justify-center backdrop-blur-sm bg-white/10">
                      <ArrowRight size={18} className="sm:w-5 sm:h-5" />
                    </div>
                  </motion.div>
                </motion.div>
              </div>
              <motion.h3
                animate={{ color: hoveredIndex === index ? '#ff5722' : '#ffffff' }}
                transition={{ duration: 0.3 }}
                className="text-xl sm:text-2xl font-bold tracking-wider mb-2"
              >
                {project.title}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="text-gray-400 text-sm tracking-wide"
              >
                {project.category}
              </motion.p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <motion.button
            ref={buttonRef}
            whileHover={{ x: 8 }}
            transition={{ duration: 0.3 }}
            className="group inline-flex items-center gap-2 sm:gap-3 text-white hover:text-orange-500 transition-colors duration-300 text-base sm:text-lg font-light tracking-wide will-change-transform"
          >
            View more work
            <motion.div
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowRight size={18} className="sm:w-5 sm:h-5" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedWork;
